import { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const Section = styled.section`
  position: relative;
  background: #0A0A0A;
  padding: 7rem 2rem 6rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 5rem 1.5rem 4.5rem;
  }
`;

/* Línea decorativa izquierda */
const SideAccent = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    #F47920 30%,
    #F47920 70%,
    transparent 100%
  );
`;

/* Fondo con patrón de puntos sutil */
const BgDots = styled.div`
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(244,121,32,0.06) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
`;

/* Gradiente que desvanece el patrón en los bordes */
const BgFade = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 70% at 50% 50%, transparent 40%, #0A0A0A 100%);
  pointer-events: none;
`;

const Inner = styled.div`
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const Eyebrow = styled(motion.div)`
  font-size: 0.7rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #F47920;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
`;

const Title = styled(motion.h2)`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(3.8rem, 7vw, 7rem);
  font-weight: 900;
  text-transform: uppercase;
  line-height: 0.92;
  color: #FFFFFF;
  margin: 0;
  letter-spacing: -0.02em;
`;

const TitleAccent = styled.span`
  color: #F47920;
`;

const OLine = styled(motion.div)`
  height: 3px;
  width: 56px;
  background: #F47920;
  border-radius: 2px;
  margin-top: 0.5rem;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;

const Para = styled(motion.p)`
  font-family: 'DM Sans', sans-serif;
  font-size: 1.08rem;
  color: #C8C8C8;
  line-height: 1.9;
  margin: 0;
  font-weight: 400;
  letter-spacing: 0.01em;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.85;
  }
`;

const Divider = styled(motion.div)`
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, rgba(244,121,32,0.5), transparent);
`;

const Tag = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  width: fit-content;
  background: #161616;
  border: 1px solid #252525;
  border-radius: 12px;
  padding: 0.7rem 1rem;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
`;

const TagIcon = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(244,121,32,0.12);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F47920;
  flex-shrink: 0;

  svg { width: 18px; height: 18px; }
`;

const TagInfo = styled.div`
  .label { font-size: 0.65rem; color: #555; text-transform: uppercase; letter-spacing: 0.08em; font-family: 'DM Sans', sans-serif; }
  .value { font-size: 0.9rem; font-weight: 700; color: #fff; font-family: 'DM Sans', sans-serif; }
`;

const VideoWrap = styled(motion.div)`
  position: relative;
  max-width: 1100px;
  margin: 4rem auto 0;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #1E1E1E;
  box-shadow: 0 24px 64px rgba(0,0,0,0.6);

  video {
    width: 100%;
    display: block;
    background: #000;
  }

  @media (max-width: 768px) {
    margin-top: 2.5rem;
    border-radius: 12px;
  }
`;

const EASE = [0.22, 1, 0.36, 1];

export default function WhoWeAre() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-12%' });

  return (
    <Section id="nosotros" ref={ref}>
      <BgDots />
      <BgFade />
      <SideAccent
        initial={{ scaleY: 0 }}
        animate={inView ? { scaleY: 1 } : { scaleY: 0 }}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
        style={{ originY: 0 }}
      />

      <Inner>
        {/* ── Columna izquierda ── */}
        <Left>
          <Eyebrow
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
          >
            Nuestra empresa
          </Eyebrow>

          <Title
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: EASE, delay: 0.25 }}
          >
            Quiénes<br />
            <TitleAccent>Somos</TitleAccent>
          </Title>

          <OLine
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.55, ease: EASE, delay: 0.45 }}
            style={{ originX: 0 }}
          />
        </Left>

        {/* ── Columna derecha ── */}
        <Right>
          <Para
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: EASE, delay: 0.35 }}
          >
            Somos una empresa especializada en la engorda y sacrificio de bovinos,
            industrialización, comercialización y distribución de carne de alta calidad.
          </Para>

          <Divider
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.5 }}
            style={{ originX: 0 }}
          />

          <Para
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: EASE, delay: 0.55 }}
          >
            Garantizamos en cada corte sabor excepcional, frescura e higiene,
            respaldados por un sistema de trazabilidad que asegura el control
            desde el origen hasta el punto de venta.
          </Para>

          <Tag
            initial={{ opacity: 0, x: -16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, ease: EASE, delay: 0.7 }}
          >
            <TagIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </TagIcon>
            <TagInfo>
              <div className="label">Sistema</div>
              <div className="value">Máxima Trazabilidad</div>
            </TagInfo>
          </Tag>

        </Right>
      </Inner>

      <VideoWrap
        initial={{ opacity: 0, y: 32 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
      >
        <video controls preload="metadata" playsInline>
          <source src="/video/quienes-somos-web.mp4" type="video/mp4" />
        </video>
      </VideoWrap>
    </Section>
  );
}
